// Core packages
import { formatDate } from '@angular/common';
import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';

// Third party packages
import { MatOptionSelectionChange, MatPaginator, MatSort } from '@angular/material';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from "file-saver";
import { combineLatest, merge, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, startWith, take, tap } from 'rxjs/operators';

// Custom packages
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from 'src/app/modules/auth/auth.service';
import { CompaniesService } from 'src/app/modules/companies/companies.service';
import { openDocumentArchiveDossierDialog } from 'src/app/modules/properties/components/document-archive-dossier-dialog/document-archive-dossier-dialog.component';
import { ExportCdDocsDialogComponent } from 'src/app/modules/properties/components/export-cd-docs-dialog/export-cd-docs-dialog.component';
import { DocumentArchiveExportDownloadService } from 'src/app/modules/properties/document-archive-export-download.service';
import {
  DocumentArchiveExportMenu,
  DocumentArchiveExportTarget,
  documentArchiveExportTargetOf,
  legacyPageExportMenu,
} from 'src/app/modules/properties/document-archive-export.utils';
import { PropertiesService } from 'src/app/modules/properties/properties.service';
import { ListSetting } from 'src/app/shared/interfaces/list-setting.interface';
import { ICompany } from 'src/app/shared/models/company/company.interface';
import { IUser } from 'src/app/shared/models/user/user.interface';
import { User } from 'src/app/shared/models/user/user.model';
import { ApiService } from 'src/app/shared/services/api.service';
import { ConfigService } from 'src/app/shared/services/config.service';
import { HelperService } from 'src/app/shared/services/helper.service';
import { ListsService } from 'src/app/shared/services/lists.service';
import { ThemeService } from 'src/app/shared/services/theme.service';
import { isArchiveV3Live } from 'src/app/shared/utils/archive-switch.utils';
import { PropertiesDataSource } from 'src/app/shared/utils/data-sources/properties-data-source';

@Component({
  selector: 'app-properties-in-structure-list-dialog',
  templateUrl: './properties-in-structure-list-dialog.component.html',
  styleUrls: ['./properties-in-structure-list-dialog.component.scss']
})
export class PropertiesInStructureListDialogComponent
  implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Table properties
  displayedColumns: string[] = [
    "index",
    "name",
    "address",
    "addressCity",
    "admin",
    "subscriptionEndDate",
    "totalDocsInserted",
    "structureOpen",
    "actions"
  ];
  dataSource: PropertiesDataSource;
  @ViewChild("sort1", { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  // @ViewChild("input", { static: true }) input: ElementRef;
  adminFilter: FormControl = new FormControl("");
  ownerFilter: FormControl = new FormControl("");
  typeFilter: FormControl = new FormControl("");
  nameFilter: FormControl = new FormControl("");
  addressFilter: FormControl = new FormControl("");
  addressCityFilter: FormControl = new FormControl("");
  subscriptionEndDateFilter: FormControl = new FormControl("");
  totalDocsInsertedFilter: FormControl = new FormControl("");
  // END - Table properties
  // BEGIN - Autocomplete fields data
  adminIsLoading = false;
  ownerIsLoading = false;
  availableAdmins: User[] = [];
  availableOwners: User[] = [];
  availableTypes = [];
  // END - Autocomplete fields data

  activeRoute: string;
  loggedUserRoleType = "";
  isInit = true;

  /** The Excel being downloaded from the new archive, `null` when none (@see exportFromArchiveV3) */
  exportingTarget: DocumentArchiveExportTarget | null = null;

  /** The download of that Excel, cancelled when the dialog closes */
  private exportSubscription: Subscription | null = null;

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<PropertiesInStructureListDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialog: MatDialog,

    private propertiesService: PropertiesService,
    private helperService: HelperService,
    private authService: AuthService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private listsService: ListsService,
    private companiesService: CompaniesService,
    private apiService: ApiService,
    private configService: ConfigService,
    private toastrService: ToastrService,
    private cookieService: CookieService,
    private documentArchiveExportDownloadService: DocumentArchiveExportDownloadService,
  ) { }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("properties.list.pageTitle")
    );

    // Get current lang
    this.lang = this.cookieService.get("lang") || this.configService.defaultLang;

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        res => (this.loggedUserPermissions = res)
      )
    );
    this.authService.loggedUserRequestedRoleType$.subscribe(
      res => {
        if (res) {
          this.loggedUserRoleType = res;
          if (this.loggedUserRoleType === "temporary") {
            let index = this.displayedColumns.findIndex((el: any) => {
              return el === "totalDocsInserted";
            })
            this.displayedColumns.splice(index, 1);
            index = this.displayedColumns.findIndex((el: any) => {
              return el === "subscriptionEndDate";
            })
            this.displayedColumns.splice(index, 1);
          }
          if (this.loggedUserRoleType === "master"
            || this.loggedUserRoleType === "operator") {
            const index = this.displayedColumns.findIndex((el: any) => {
              return el === "admin";
            })
            this.displayedColumns.splice(index, 1);
          }

          if (!this.isInit) {
            this.dataSource = new PropertiesDataSource(this.propertiesService, this.helperService);
            this.loadPage();
          }
        }
      }
    )


    // Init data source
    this.dataSource = new PropertiesDataSource(
      this.propertiesService,
      this.helperService
    );

    // Subscribe to listsService and update this list settings
    this.subscribeToListsSettings("properties");

    // Get available admin and owners from users' list
    this.getAvailableAdminsAndOwners();

    // Get available property types from configService
    this.availableTypes = this.configService.availablePropertyTypes;
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // Load first set of data
    this.loadPage();

    // Reset pagination after sorting
    this.subscriptions.push(
      this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0))
    );

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => {
            this.loadPage();
          })
        )
        .subscribe()
    );

    // Handle server-side filtering through general search
    // this.subscriptions.push(
    //   fromEvent(this.input.nativeElement, "keyup")
    //     .pipe(
    //       debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
    //       distinctUntilChanged(), // Eliminate duplicate values
    //       tap(() => {
    //         this.paginator.pageIndex = 0; // Reset page
    //         this.loadPage();
    //       })
    //     )
    //     .subscribe()
    // );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.adminFilter.valueChanges.pipe(startWith(null as string)),
        this.ownerFilter.valueChanges.pipe(startWith(null as string)),
        this.typeFilter.valueChanges.pipe(startWith(null as string)),
        this.nameFilter.valueChanges.pipe(startWith(null as string)),
        this.addressFilter.valueChanges.pipe(startWith(null as string)),
        this.addressCityFilter.valueChanges.pipe(startWith(null as string)),
        this.subscriptionEndDateFilter.valueChanges.pipe(startWith(null as string)),
        this.totalDocsInsertedFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged() // Eliminate duplicate values
        )
        .subscribe(res => {
          // Handle admin filter
          if (res[0]) {
            // Since we cannot search in the same time for both admin and
            // owner, we must reset admin field. Maybe this should be handled in a better way
            this.ownerFilter.setValue(undefined, { emitEvent: false });
            this.adminIsLoading = true;
            this.getAvailableAdminsAndOwners("admin", {
              general: this.adminFilter.value
            });
            return;
          }
          // Handle owner filter
          if (res[1]) {
            // Since we cannot search in the same time for both admin and
            // owner, we must reset admin field. Maybe this should be handled in a better way
            this.adminFilter.setValue(undefined, { emitEvent: false });
            this.ownerIsLoading = true;
            this.getAvailableAdminsAndOwners("owner", {
              general: this.ownerFilter.value
            });
            return;
          }

          // Load page only if at least one value is not null
          if (!res.every(value => value === null)) {
            this.paginator.pageIndex = 0;
            this.loadPage();
          }
        })
    );

    this.isInit = false;
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.exportSubscription) {
      this.exportSubscription.unsubscribe();
    }

    // Push current list settings to the listsService
    this.listsService.setListSetting(
      "properties",
      this.paginator.pageIndex.valueOf(),
      this.paginator.pageSize.valueOf(),
      {
        general: '', // this.input.nativeElement.value,
        admin: this.adminFilter.value,
        owner: this.ownerFilter.value,
        type: this.typeFilter.value,
        name: this.nameFilter.value,
        address: this.addressFilter.value,
        addressCity: this.addressCityFilter.value,
        subscriptionEndDate: this.subscriptionEndDateFilter.value,
        totalDocsInserted: this.totalDocsInsertedFilter.value,
      }
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadPage(): void {
    if (!this.data.topicKey) {
      this.toastrService.error("Error: no topicKey injected!")
      return;
    }
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const length = this.paginator.pageSize;
    const search = {
      general: '', // this.input.nativeElement.value,
      admin: this.adminFilter.value ? this.adminFilter.value._id : undefined,
      owner: this.ownerFilter.value ? this.ownerFilter.value._id : undefined,
      type: this.typeFilter.value,
      name: this.nameFilter.value,
      address: this.addressFilter.value,
      addressCity: this.addressCityFilter.value,
      subscriptionEndDate: this.subscriptionEndDateFilter.value,
      totalDocsInserted: this.totalDocsInsertedFilter.value,
    };
    this.dataSource.loadInStructureItems(
      this.data.topicKey,
      start,
      length,
      this.sort.active,
      this.sort.direction,
      search
    );
  }

  /**
   * Export properties in a file with given format
   *
   * @since 1.0.0
   */
  onExport(target?: string, propertyId?: string): void {
    // With the switch live, the two Excels of a property come from the new archive (NEW-M7)
    const archiveV3Target = isArchiveV3Live() ? documentArchiveExportTargetOf(target) : null;
    if (archiveV3Target !== null) {
      this.exportFromArchiveV3(archiveV3Target, propertyId);
      return;
    }

    // If target is not provided, than just export all properties
    if (!target) {
      const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
      const fileName = `${this.translateService.instant(
        "properties.list.exportFileName"
      )} ${date}`;
      this.subscriptions.push(
        this.propertiesService.getExportInStructureList(this.data.topicKey).subscribe(
          res => {
            const fileBlob = res;
            const blob = new Blob([fileBlob], {
              type: "application/vnd.ms-excel"
            });
            FileSaver.saveAs(blob, `${fileName}.xlsx`);
          },
          err => {
            this.helperService.handleError(err);
          }
        )
      );
    } else if (target && typeof propertyId !== "undefined") {
      // Handle export for missing/inserted documents
      const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
      const fileName = `${this.translateService.instant(
        `properties.list.${target}`
      )} ${date}`;
      this.subscriptions.push(
        this.propertiesService.getDocExport(target, propertyId).subscribe(
          res => {
            const fileBlob = res;
            const blob = new Blob([fileBlob], {
              type: "application/vnd.ms-excel"
            });
            FileSaver.saveAs(blob, `${fileName}.xlsx`);
          },
          err => {
            this.helperService.handleError(err);
          }
        )
      );
    }
  }

  /**
   * The «Esporta» entries of the «Apri» menu of a property as far as the switch decides them: both,
   * under the rules of the template, until the switch; once it is live, those of the «Esporta» menu of
   * the new dossier, for the plan of that property (@see legacyPageExportMenu). A property with no
   * plan counts as a paid one, as in the legacy.
   *
   * @since 3.0.0
   *
   * @param element The property of the row; `null` for the rules of the role alone, as on a paid plan
   */
  downloadMenuFor(element: { subscription?: { key?: unknown } } | null): DocumentArchiveExportMenu {
    const subscription = element ? element.subscription : undefined;
    return legacyPageExportMenu(isArchiveV3Live(), {
      roleType: this.loggedUserRoleType,
      permissions: this.loggedUserPermissions,
      subscriptionKey: subscription && typeof subscription.key === "string" ? subscription.key : "",
    });
  }

  /**
   * «Esporta inseriti» and «Esporta mancanti» of a property with the switch live: the Excel of the new
   * archive, as the «Esporta» menu of its dossier gives it. One download at a time. The plan of the row
   * is checked by the template; the role is checked again here, as the dossier does: a dialog left open
   * across a role change must not ask for an Excel the new role is not shown.
   *
   * @since 3.0.0
   *
   * @param target     Which Excel
   * @param propertyId The property of the row
   */
  private exportFromArchiveV3(target: DocumentArchiveExportTarget, propertyId?: string): void {
    if (this.exportingTarget !== null || !propertyId || !this.downloadMenuFor(null)[target]) {
      return;
    }
    this.exportingTarget = target;
    if (this.exportSubscription) {
      this.exportSubscription.unsubscribe();
    }
    this.exportSubscription = this.documentArchiveExportDownloadService
      .download(target, propertyId)
      .pipe(
        finalize(() => {
          this.exportingTarget = null;
        })
      )
      .subscribe();
  }

  /**
   * CD Export file
   * export the files of all condominium documents in zip format
   *
   * @since 1.0.0
   */
  onExportFile(propertyId?: string, propertyName?: string): void {
    // With the 3.0.0 archive switched on the dossier comes from it, part by part (NEW-M17)
    if (isArchiveV3Live()) {
      openDocumentArchiveDossierDialog(this.dialog, propertyId);
      return;
    }
    const dialogRef = this.dialog.open(ExportCdDocsDialogComponent, {
      minWidth: "50%",
      maxHeight: "98vh",
      data: {
        propertyId: propertyId,
        propertyName: propertyName,
      },
      disableClose: true,
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }

        return;
      });
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting("", 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = "";
    this.adminFilter.setValue(null);
    this.ownerFilter.setValue(null);
    this.typeFilter.setValue(null);
    this.nameFilter.setValue(null);
    this.addressFilter.setValue(null);
    this.addressCityFilter.setValue(null);
    this.subscriptionEndDateFilter.setValue(null);
    this.totalDocsInsertedFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.sort.direction = "desc";
    this.sort.active = "subscriptionEndDate";
    this.sort._stateChanges.next();

    // Reset paginator
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;

    // Reload table's data
    this.loadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilter(filter: FormControl): void {
    filter.reset();
    this.loadPage();
  }

  /**
   * Subscribe to lists settings service to allow this list
   * to remember current filters once users navigate to another page.
   * In this way once user will come back to this page he will
   * have all previous settings (such as page number, page size, search, ...)
   *
   * @since 1.0.0
   */
  subscribeToListsSettings(name: string): void {
    this.listsService.enable();
    this.subscriptions.push(
      this.listsService.listsSettings$.subscribe((settings: ListSetting[]) => {
        if (settings.length) {
          settings.forEach((setting: ListSetting) => {
            if (setting.name === name) {
              if (setting.pageIndex) {
                this.paginator.pageIndex = setting.pageIndex;
              }
              if (setting.pageSize) {
                this.paginator.pageSize = setting.pageSize;
              }
              if (setting.search && setting.search.general) {
                // this.input.nativeElement.value = setting.search.general;
              }
              if (setting.search && setting.search.admin) {
                this.adminFilter.setValue(setting.search.admin);
              }
              if (setting.search && setting.search.owner) {
                this.ownerFilter.setValue(setting.search.owner);
              }
              if (setting.search && setting.search.type) {
                this.typeFilter.setValue(setting.search.type);
              }
              if (setting.search && setting.search.name) {
                this.nameFilter.setValue(setting.search.name);
              }
              if (setting.search && setting.search.address) {
                this.addressFilter.setValue(setting.search.address);
              }
              if (setting.search && setting.search.addressCity) {
                this.addressCityFilter.setValue(setting.search.addressCity);
              }
              if (setting.search && setting.search.subscriptionEndDate) {
                this.subscriptionEndDateFilter.setValue(setting.search.subscriptionEndDate);
              }
              if (setting.search && setting.search.totalDocsInserted) {
                this.totalDocsInsertedFilter.setValue(setting.search.totalDocsInserted);
              }
            }
          });
        }
      })
    );
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInput(): void {
    // this.input.nativeElement.value = "";
    const event = new KeyboardEvent("keyup");
    // this.input.nativeElement.dispatchEvent(event);
  }

  /**
   * Get the list of available admins and owners
   * used to allow filtering on owner and admin columns
   *
   * @since 1.0.0
   */
  getAvailableAdminsAndOwners(type?: string, search?: any): void {
    this.subscriptions.push(
      this.companiesService
        .getList(0, 10, null, null, search)
        .pipe(
          finalize(() => {
            this.adminIsLoading = false;
            this.ownerIsLoading = false;
          })
        )
        .subscribe(res => {
          if (res.status) {
            if (type === "admin") {
              this.availableAdmins = res.data.items;
            } else if (type === "owner") {
              this.availableOwners = res.data.items;
            } else {
              this.availableAdmins = res.data.items;
              this.availableOwners = res.data.items;
            }
          }
        })
    );
  }

  /**
   * @todo
   * TEST MAT-AUTOCOMPLETE FIELDS (ADMIN_FILTER AND OWNER_FILTER) ONCE WE HAVE MORE THAN 10
   * USERS. WE NEED TO CHECK IF PAGINATIONS OF USERS INSIDE THIS MAT-AUTOCOMPLETE IS WORKING
   * PROPERLY OR NOT.
   */

  /**
   * Handle click on a selected admin in the mat-autocomplete list and fires the loadPage()
   *
   * @since 1.0.0
   */
  onAdminFilterSelected(event: MatOptionSelectionChange): void {
    const value = event.source.value;
    this.adminFilter.setValue(value, { emitEvent: false });
    this.ownerFilter.setValue(undefined, { emitEvent: false });
    this.loadPage();
  }

  /**
   * Handle click on a selected owner in the mat-autocomplete list and fires the loadPage()
   *
   * @since 1.0.0
   */
  onOwnerFilterSelected(event: MatOptionSelectionChange): void {
    const value = event.source.value;
    this.adminFilter.setValue(undefined, { emitEvent: false });
    this.ownerFilter.setValue(value, { emitEvent: false });
    this.loadPage();
  }

  /**
   * Used to handle the display of selected result
   *
   * @since 1.0.0
   *
   * @param arg Data to be displayed
   * @returns result string to display or undefined
   */
  displayAdminOrOwnerFn(arg?: ICompany): string | undefined {
    return arg ? `${arg.name}` : undefined;
  }

  /**
   * Handle click on a selected owner in the mat-select list and fires the loadPage()
   *
   * @since 1.0.0
   */
  // onTypeFilterSelected(event: MatOptionSelectionChange): void {
  //   const value = event.source.value;
  //   this.typeFilter.setValue(value, { emitEvent: false });
  //   this.loadPage();
  // }

  /**
   * Used to handle the display of selected result
   *
   * @since 1.0.0
   *
   * @param arg Data to be displayed
   * @returns result string to display or undefined
   */
  displayTypeFn(arg?: string): string | undefined {
    return arg
      ? `${this.translateService.instant("properties.types." + arg)}`
      : undefined;
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   *
   * @param [data] optional data passed to the caller component
   */
  onClose(data?: any): void {
    this.dialogRef.close(data);
  }
  
}
